import { vars } from '../../../theme';

export type KanbanCardState = 'default' | 'hovered' | 'pressed' | 'dragged';
export type KanbanCardStyleConfig = {
    background: string;
    border: string;
};

/**
 * Configuration for Kanban card background and box shadow styles.
 *
 * Organized by:
 * - state: 'default' | 'hovered' | 'pressed' | 'dragged'
 * - selected: true | false
 * - selectionMode: true | false
 *
 * Total: 4 states × 2 selected states × 2 selection modes = 16 combinations
 */
export const kanbanCardStyleConfig: Record<
    KanbanCardState,
    Record<
        'selected' | 'unselected',
        Record<'selectionMode' | 'normal', KanbanCardStyleConfig>
    >
> = {
    default: {
        unselected: {
            normal: {
                background: vars.colors.surface.item,
                border: vars.colors.stroke.secondary
            },
            selectionMode: {
                background: vars.colors.surface.item,
                border: vars.colors.stroke.secondary
            }
        },
        selected: {
            normal: {
                background: vars.colors.surface.item,
                border: vars.colors.base.accent
            },
            selectionMode: {
                background: vars.colors.surface.accent,
                border: vars.colors.base.accent
            }
        }
    },
    hovered: {
        unselected: {
            normal: {
                background: vars.colors.surface.item,
                border: vars.colors.stroke.primary
            },
            selectionMode: {
                background: vars.colors.surface.item,
                border: vars.colors.stroke.primary
            }
        },
        selected: {
            normal: {
                background: vars.colors.surface.item,
                border: vars.colors.base.accent
            },
            selectionMode: {
                background: vars.colors.surface.accent,
                border: vars.colors.base.accent
            }
        }
    },
    pressed: {
        unselected: {
            normal: {
                background: vars.colors.surface.item,
                border: vars.colors.stroke.selected
            },
            selectionMode: {
                background: vars.colors.surface.item,
                border: vars.colors.stroke.selected
            }
        },
        selected: {
            normal: {
                background: vars.colors.surface.item,
                border: vars.colors.base.accent
            },
            selectionMode: {
                background: vars.colors.surface.accent,
                border: vars.colors.base.accent
            }
        }
    },
    dragged: {
        unselected: {
            normal: {
                background: vars.colors.surface.item,
                border: vars.colors.base.accent
            },
            selectionMode: {
                background: vars.colors.surface.item,
                border: vars.colors.base.accent
            }
        },
        selected: {
            normal: {
                background: vars.colors.surface.item,
                border: vars.colors.base.accent
            },
            selectionMode: {
                background: vars.colors.surface.accent,
                border: vars.colors.base.accent
            }
        }
    }
};

/**
 * Helper function to get the style configuration for a Kanban card
 * @param state - The interaction state: 'default', 'hovered', 'pressed', or 'dragged'
 * @param selected - Whether the card is selected
 * @param selectionMode - Whether selection mode is active
 * @returns The background, border, and boxShadow configuration
 */
const getStyleByState = (
    state: KanbanCardState,
    selected: boolean,
    selectionMode: boolean
): { background: string; border: string; boxShadow: string } => {
    const selectedKey = selected ? 'selected' : 'unselected';
    const modeKey = selectionMode ? 'selectionMode' : 'normal';
    const { border, background } =
        kanbanCardStyleConfig[state][selectedKey][modeKey];
    // Soft elevation ring kept as rgba(0,0,0,0.02) — not elevation token
    const boxShadow = `0 2px 4px 0 rgba(0, 0, 0, 0.02), 0 0 0 1px ${border}`;
    return {
        background,
        border,
        boxShadow
    };
};

export const getKanbanCardStyles = (props: {
    $selected: boolean;
    $isInSelectableState: boolean;
    $isDragging: boolean;
    $isOverlay?: boolean;
    $draggingOverNonDroppable?: boolean;
}) => {
    const {
        $selected,
        $isInSelectableState,
        $isDragging,
        $isOverlay,
        $draggingOverNonDroppable
    } = props;

    // Apply dragged styles when actually dragging OR when it's the drag overlay
    if ($isDragging || $isOverlay) {
        const draggedStyle = getStyleByState(
            'dragged',
            $selected,
            $isInSelectableState
        );
        // Make the original card semi-transparent while dragging (overlay shows the floating version)
        const opacity = $isOverlay ? '1' : '0.3';

        // If dragging over non-droppable column, change cursor and border color
        const cursor = $draggingOverNonDroppable ? 'not-allowed' : 'grab';
        const borderColor = $draggingOverNonDroppable
            ? vars.colors.stroke.secondary
            : draggedStyle.border;
        const boxShadow = `0 2px 4px 0 rgba(0, 0, 0, 0.02), 0 0 0 1px ${borderColor}`;

        return `
            background: ${draggedStyle.background};
            box-shadow: ${boxShadow};
            opacity: ${opacity};
            cursor: ${cursor};
        `;
    }

    const defaultStyle = getStyleByState(
        'default',
        $selected,
        $isInSelectableState
    );
    const hoverStyle = getStyleByState(
        'hovered',
        $selected,
        $isInSelectableState
    );
    const pressedStyle = getStyleByState(
        'pressed',
        $selected,
        $isInSelectableState
    );

    return `
        background: ${defaultStyle.background};
        box-shadow: ${defaultStyle.boxShadow};
        
        &:hover {
            background: ${hoverStyle.background};
            box-shadow: ${hoverStyle.boxShadow};
        }
        
        &:active {
            background: ${pressedStyle.background};
            box-shadow: ${pressedStyle.boxShadow};
        }
    `;
};
